import { MAX_FILE_SIZE } from 'constants/constants';
import { getValidationMessage } from './messages';

interface ValidationOptions {
    maxMbFileSize?: number;
    sizeError?: string;
    fileFormatError?: string;
    fileTypesAllowed?: string;
}

/**
 * Validates a list of files for size and format.
 * @param data - The file(s) to validate (FileList or File array)
 * @param additionalOptions - Configuration for size limits and allowed types
 * @returns An array of error messages, a single mandatory error string, or null if valid.
 */
export const validateFile = (
    data: FileList | File[] | null | undefined,
    additionalOptions: ValidationOptions = {},
): string | string[] | null => {
    const {
        maxMbFileSize,
        sizeError,
        fileFormatError,
        fileTypesAllowed,
    } = additionalOptions;

    const maxFileSize = maxMbFileSize || MAX_FILE_SIZE;

    if (!data || data.length === 0) {
        return getValidationMessage().mandatoryError;
    }

    const results: string[] = Array.from(data)
        .map((file: File) => {
            // Check file type/mime
            if (fileTypesAllowed && !fileTypesAllowed.includes(file.type)) {
                return fileFormatError || getValidationMessage().format;
            }

            // Convert size to MB
            const fileSize = file.size / 1024 / 1024;

            if (fileSize > maxFileSize) {
                return sizeError || getValidationMessage().size;
            }

            return null;
        })
        .filter((error): error is string => !!error); // Type guard to filter out nulls

    return results.length === 0 ? null : results;
};

/**
 * Checks if the upload count exceeds the maximum allowed.
 * @param invalid - Boolean check for count validity
 * @param numberOfFiles - The limit count for the error message
 */
export const validateUploadCount = (invalid: boolean, numberOfFiles: number): string | null => {
    if (invalid) {
        return getValidationMessage(numberOfFiles).maxUploadCount;
    }

    return null;
};
